import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { MagnifyingGlassIcon, FunnelIcon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { binService } from "../../services/binService";
import { anonymousBinService } from "../../utils/anonymousBinService";
import Slideshow from "../Common/Slideshow";
import ValidatedBinChangeModal from "../Common/ValidatedBinChangeModal";
import { readCache, writeCache } from "../../utils/readCache";
import LogoLoader from "../Common/LogoLoader";
import { useMaterials, useBin } from "../../query/hooks";
import { queryKeys } from "../../query/keys";
import { binFromPayload } from "../../query/bin";

const MaterialList = () => {
  const materialsQuery = useMaterials();
  const materials = useMemo(() => {
    if (Array.isArray(materialsQuery.data)) return materialsQuery.data;
    const cached = readCache("materials");
    return Array.isArray(cached) ? cached : [];
  }, [materialsQuery.data]);
  const loading = materialsQuery.isLoading && materials.length === 0;
  const loadError = materialsQuery.isError && materials.length === 0
    ? "Materials could not be loaded. Check your connection and try again."
    : "";
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [validatedWarningOpen, setValidatedWarningOpen] = useState(false);
  const [pendingMaterial, setPendingMaterial] = useState(null);
  const [confirmingAdd, setConfirmingAdd] = useState(false);
  const [anonBin, setAnonBin] = useState(() => anonymousBinService.getBin());
  const [localQty, setLocalQty] = useState({});
  const qtyGeneration = useRef({});
  const qtyQueue = useRef({});
  const intendedQty = useRef({});
  const { user } = useAuth();
  const binQuery = useBin(!!user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "househole_wastes", label: "Household Wastes" },
    { value: "industrial_waste", label: "Industrial Waste" },
    { value: "raw_meterials", label: "Raw Materials" },
  ];

  useEffect(() => {
    if (Array.isArray(materialsQuery.data)) writeCache("materials", materialsQuery.data);
  }, [materialsQuery.data]);

  useEffect(() => {
    const sync = () => setAnonBin(anonymousBinService.getBin());
    window.addEventListener("jhonks-anonymous-bin-changed", sync);
    return () => window.removeEventListener("jhonks-anonymous-bin-changed", sync);
  }, []);

  const activeBin = user ? binQuery.data : anonBin;

  const materialKey = (item) => {
    const material = item?.material;
    if (!material) return "";
    if (typeof material === "string") return String(material);
    return String(material._id || material.id || "");
  };

  const quantityFromBin = (materialId) => {
    const item = (activeBin?.materials || []).find((entry) => materialKey(entry) === String(materialId));
    return Number(item?.quantity || 0);
  };

  const quantityFor = (materialId) => {
    if (localQty[materialId] != null) return Number(localQty[materialId]);
    return quantityFromBin(materialId);
  };

  const binTotals = useMemo(() => {
    const lines = activeBin?.materials || [];
    const seen = new Set();
    const totals = lines.reduce(
      (sum, item) => {
        const id = (() => {
          const material = item?.material;
          if (!material) return "";
          if (typeof material === "string") return String(material);
          return String(material._id || material.id || "");
        })();
        if (id) seen.add(id);
        const qty = localQty[id] != null ? Number(localQty[id]) : Number(item.quantity || 0);
        const unit = Number(item.material?.pricePerKg || 0);
        const linePrice = Number(item.price);
        sum.kg += qty;
        sum.price += localQty[id] != null ? qty * unit : (Number.isFinite(linePrice) && linePrice > 0 ? linePrice : qty * unit);
        return sum;
      },
      { kg: 0, price: 0 }
    );
    Object.entries(localQty).forEach(([id, qty]) => {
      if (seen.has(id) || Number(qty) <= 0) return;
      const material = materials.find((entry) => String(entry._id) === String(id));
      totals.kg += Number(qty);
      totals.price += Number(qty) * Number(material?.pricePerKg || 0);
    });
    return totals;
  }, [activeBin, localQty, materials]);

  const filteredMaterials = useMemo(() => {
    let filtered = materials;
    if (searchTerm) {
      const needle = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (material) =>
          material.name.toLowerCase().includes(needle) ||
          material.category.toLowerCase().includes(needle)
      );
    }
    if (selectedCategory !== "all") {
      filtered = filtered.filter((material) => material.category === selectedCategory);
    }
    return filtered;
  }, [materials, searchTerm, selectedCategory]);

  const applyQuantity = async (material, nextQty, options = {}) => {
    const id = material._id;
    const skipWarning = options.skipWarning;
    let validated = !!activeBin?.validationStatus;
    if (user && !skipWarning && !binQuery.data) {
      try {
        const raw = await queryClient.fetchQuery({
          queryKey: queryKeys.bin,
          queryFn: async () => binFromPayload(await binService.getUserBin()),
        });
        validated = !!raw?.validationStatus;
      } catch (error) {
        console.error("Bin check failed:", error);
      }
    }
    if (user && validated && !skipWarning) {
      setPendingMaterial({ material, nextQty });
      setValidatedWarningOpen(true);
      return;
    }
    const ticket = (qtyGeneration.current[id] = (qtyGeneration.current[id] || 0) + 1);
    intendedQty.current[id] = nextQty;
    setLocalQty((current) => ({ ...current, [id]: nextQty }));
    qtyQueue.current[id] = (qtyQueue.current[id] || Promise.resolve()).then(async () => {
      try {
        if (!user) {
          setAnonBin(anonymousBinService.setQuantity({ material, quantity: nextQty }));
        } else {
          const payload = await binService.setQuantity(id, nextQty);
          queryClient.setQueryData(queryKeys.bin, binFromPayload(payload));
        }
        if (typeof window !== "undefined" && window.refreshNavbarCounts) {
          window.refreshNavbarCounts();
        }
      } catch (e) {
        console.error("Quick add failed:", e);
      } finally {
        if (qtyGeneration.current[id] === ticket) {
          delete intendedQty.current[id];
          setLocalQty((current) => {
            const next = { ...current };
            delete next[id];
            return next;
          });
        }
      }
    });
  };

  const stepQuantity = (material, delta) => {
    const id = material._id;
    const current = intendedQty.current[id] != null ? Number(intendedQty.current[id]) : quantityFromBin(id);
    applyQuantity(material, Math.max(0, current + delta));
  };

  const handleConfirmValidatedAdd = async () => {
    if (!pendingMaterial?.material) return;
    setConfirmingAdd(true);
    try {
      applyQuantity(pendingMaterial.material, pendingMaterial.nextQty, { skipWarning: true });
      setValidatedWarningOpen(false);
      setPendingMaterial(null);
    } finally {
      setConfirmingAdd(false);
    }
  };

  return (
    <div className={`space-y-6 ${binTotals.kg > 0 ? "pb-24" : ""}`}>
      <ValidatedBinChangeModal
        open={validatedWarningOpen}
        onCancel={() => {
          if (confirmingAdd) return;
          setValidatedWarningOpen(false);
          setPendingMaterial(null);
        }}
        onConfirm={handleConfirmValidatedAdd}
        confirming={confirmingAdd}
      />
      <Slideshow />
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Available Materials
        </h1>
        <p className="text-gray-600">
          Browse through our list of materials we buy. Click on any material to
          view details and add to your bin.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search materials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-40">
          <LogoLoader />
        </div>
      )}

      {loadError && !loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-700">{loadError}</p>
          <button
            type="button"
            onClick={() => materialsQuery.refetch()}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md"
          >
            Try again
          </button>
        </div>
      )}

      {/* Materials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredMaterials.map((material) => (
          <div
            key={material._id}
            className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
          >
            <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-t-lg">
                {material.image ? (
                <img
                  src={material.image}
                  alt={material.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-48 object-cover rounded-t-lg"
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 rounded-t-lg flex items-center justify-center">
                  <span className="text-gray-500">No Image</span>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {material.name}
                </h3>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  {material.category
                    .replace("_", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              </div>
              <p className="text-2xl font-bold text-green-600 mb-4">
                ₦{material.pricePerKg?.toFixed(2) || "0.00"}/kg
              </p>
              <div className="space-y-2">
                <Link
                  to={`/materials/${material._id}`}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-center block"
                >
                  Check Details
                </Link>

                {quantityFor(material._id) > 0 ? (
                  <div className="flex items-center justify-between rounded-md border border-green-600">
                    <button
                      type="button"
                      onClick={() => stepQuantity(material, -1)}
                      className="h-10 w-12 text-xl font-semibold text-green-700 hover:bg-green-50"
                      aria-label={`Reduce ${material.name}`}
                    >
                      −
                    </button>
                    <span className="text-sm font-semibold text-gray-900">{quantityFor(material._id)} KG</span>
                    <button
                      type="button"
                      onClick={() => stepQuantity(material, 1)}
                      className="h-10 w-12 text-xl font-semibold text-green-700 hover:bg-green-50"
                      aria-label={`Increase ${material.name}`}
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => stepQuantity(material, 1)}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-green-600/30 py-2 px-4 font-medium text-green-700 transition-colors hover:border-green-700"
                    aria-label={`Add ${material.name} to bin`}
                  >
                    <ShoppingCartIcon className="h-5 w-5" />
                    Add to Bin
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {!loading && !loadError && filteredMaterials.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600 text-lg">
            {searchTerm || selectedCategory !== "all"
              ? "No materials found matching your criteria."
              : "No materials available at the moment."}
          </p>
          {(searchTerm || selectedCategory !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("all");
              }}
              className="mt-4 text-green-600 hover:text-green-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {binTotals.kg > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.08)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
            <div>
              <p className="text-sm text-gray-500">In your bin</p>
              <p className="text-base font-semibold text-gray-900">
                {binTotals.kg} kg · ₦{Number(binTotals.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/bin")}
              className="rounded-full bg-green-600 px-5 py-3 text-sm font-semibold text-white hover:bg-green-700"
            >
              View Bin
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialList;
